// Per-vertical filter + sort declarations consumed by the FILTER ENGINE.
// Each vertical only declares filters/sorts backed by real normalized data; anything a
// provider doesn't supply is simply omitted (the engine also auto-hides empty facets),
// so we never fabricate unsupported filter values.
//
// SORTS ARE COMMISSION-BLIND: comparators reference only customer-facing fields.
import { formatPrice } from './currency';

const priceFmt = (v, cur) => formatPrice(v, cur);
const num = (v) => (typeof v === 'number' && isFinite(v) ? v : null);
const pretty = (s) => String(s || '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const esimDataMb = (o) => (o.unlimited ? null : num(o.data_mb));
const esimValue = (o) => (o.unlimited ? (o.price_inr || 0) / (50 * 1024) : (o.price_inr || 0) / Math.max(1, o.data_mb || 1));

export const SCHEMAS = {
  hotel: {
    key: 'hotel', idField: 'offer_id', defaultSort: 'recommended',
    sorts: [
      { key: 'recommended', label: 'Recommended', cmp: (a, b) => (b.stars || 0) - (a.stars || 0) || (b.review_score || 0) - (a.review_score || 0) },
      { key: 'price_low', label: 'Lowest price', cmp: (a, b) => (a.price_inr || 0) - (b.price_inr || 0) },
      { key: 'price_high', label: 'Highest price', cmp: (a, b) => (b.price_inr || 0) - (a.price_inr || 0) },
      { key: 'rating', label: 'Rating', cmp: (a, b) => (b.review_score || 0) - (a.review_score || 0) },
      { key: 'distance', label: 'Distance', requiresField: 'distance_km', cmp: (a, b) => (a.distance_km ?? Infinity) - (b.distance_km ?? Infinity) },
    ],
    filters: [
      { key: 'price', type: 'range', label: 'Price', accessor: (o) => num(o.price_inr), format: priceFmt },
      { key: 'stars', type: 'multi', label: 'Star rating', accessor: (o) => o.stars, optionLabel: (v) => `${v}★` },
      { key: 'guest', type: 'range', label: 'Guest rating', accessor: (o) => num(o.review_score), format: (v) => `${v}` },
      { key: 'distance', type: 'range', label: 'Distance from centre (km)', accessor: (o) => num(o.distance_km), format: (v) => `${v}km` },
      { key: 'room', type: 'multi', label: 'Room type', accessor: (o) => o.room_type },
      { key: 'breakfast', type: 'toggle', label: 'Breakfast included', predicate: (o) => !!o.include_breakfast },
      { key: 'wifi', type: 'toggle', label: 'Free Wi-Fi', predicate: (o) => !!o.free_wifi },
    ],
  },

  experience: {
    key: 'experience', idField: 'offer_id', defaultSort: 'recommended',
    sorts: [
      { key: 'recommended', label: 'Recommended', cmp: (a, b) => (b.rating || 0) * Math.log10((b.review_count || 0) + 10) - (a.rating || 0) * Math.log10((a.review_count || 0) + 10) },
      { key: 'price_low', label: 'Lowest price', cmp: (a, b) => (a.price_inr || 0) - (b.price_inr || 0) },
      { key: 'rating', label: 'Highest rated', cmp: (a, b) => (b.rating || 0) - (a.rating || 0) },
      { key: 'popular', label: 'Most popular', cmp: (a, b) => (b.review_count || 0) - (a.review_count || 0) },
    ],
    filters: [
      { key: 'price', type: 'range', label: 'Price', accessor: (o) => num(o.price_inr), format: priceFmt },
      { key: 'rating', type: 'range', label: 'Rating', accessor: (o) => num(o.rating), format: (v) => `${v}★` },
      { key: 'duration', type: 'range', label: 'Duration (hrs)', accessor: (o) => num(o.duration_hours), format: (v) => `${v}h` },
      { key: 'category', type: 'multi', label: 'Category', accessor: (o) => o.category },
    ],
  },

  tour: {
    key: 'tour', idField: 'tour_id', defaultSort: 'recommended',
    sorts: [
      { key: 'recommended', label: 'Recommended', cmp: (a, b) => (b.rating || 0) - (a.rating || 0) },
      { key: 'price_low', label: 'Lowest price', cmp: (a, b) => (a.best_price || 0) - (b.best_price || 0) },
      { key: 'rating', label: 'Highest rated', cmp: (a, b) => (b.rating || 0) - (a.rating || 0) },
      { key: 'dur_short', label: 'Shortest', cmp: (a, b) => tourDays(a) - tourDays(b) },
      { key: 'dur_long', label: 'Longest', cmp: (a, b) => tourDays(b) - tourDays(a) },
    ],
    filters: [
      { key: 'price', type: 'range', label: 'Price', accessor: (o) => num(o.best_price), format: priceFmt },
      { key: 'days', type: 'range', label: 'Duration (days)', accessor: (o) => num(tourDays(o)) || null, format: (v) => `${v}d` },
      { key: 'destination', type: 'multi', label: 'Destination', accessor: (o) => o.country },
      { key: 'type', type: 'multi', label: 'Tour type', accessor: (o) => o.categories },
      { key: 'rating', type: 'range', label: 'Rating', accessor: (o) => num(o.rating), format: (v) => `${v}★` },
      { key: 'freecancel', type: 'toggle', label: 'Free cancellation', predicate: (o) => o.best_cancellation === 'free' },
      { key: 'provider', type: 'multi', label: 'Provider', accessor: (o) => (o.partner_ids || o.partners || []).map((p) => (p.partner_id || p)) },
    ],
  },

  esim: {
    key: 'esim', idField: 'id', defaultSort: 'recommended',
    sorts: [
      { key: 'recommended', label: 'Recommended', cmp: (a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || (a.price_inr || 0) - (b.price_inr || 0) },
      { key: 'cheapest', label: 'Lowest price', cmp: (a, b) => (a.price_inr || 0) - (b.price_inr || 0) },
      { key: 'most_data', label: 'Most data', cmp: (a, b) => (b.unlimited ? 1 : 0) - (a.unlimited ? 1 : 0) || (b.data_mb || 0) - (a.data_mb || 0) },
      { key: 'longest', label: 'Longest validity', cmp: (a, b) => (b.duration_days || 0) - (a.duration_days || 0) },
      { key: 'best_value', label: 'Best value', cmp: (a, b) => esimValue(a) - esimValue(b) },
    ],
    filters: [
      { key: 'price', type: 'range', label: 'Price', accessor: (o) => num(o.price_inr), format: priceFmt },
      { key: 'data', type: 'range', label: 'Data (MB)', accessor: esimDataMb, format: (v) => `${(v / 1024).toFixed(v >= 1024 ? 0 : 1)}GB` },
      { key: 'validity', type: 'range', label: 'Validity (days)', accessor: (o) => num(o.duration_days), format: (v) => `${v}d` },
      { key: 'unlimited', type: 'toggle', label: 'Unlimited data', predicate: (o) => !!o.unlimited },
      { key: 'fiveg', type: 'toggle', label: '5G', predicate: (o) => !!o.is_5g },
      { key: 'hotspot', type: 'toggle', label: 'Hotspot', predicate: (o) => !!o.hotspot },
      { key: 'provider', type: 'multi', label: 'Provider', accessor: (o) => o.provider_name, optionLabel: pretty },
    ],
  },

  store: {
    key: 'store', idField: 'id', defaultSort: 'recommended',
    sorts: [
      { key: 'recommended', label: 'Recommended', cmp: (a, b) => (b.editorial_priority || 0) - (a.editorial_priority || 0) },
      { key: 'price_low', label: 'Lowest price', cmp: (a, b) => (a.best_price || 0) - (b.best_price || 0) },
      { key: 'price_high', label: 'Highest price', cmp: (a, b) => (b.best_price || 0) - (a.best_price || 0) },
      { key: 'best_value', label: 'Best value', cmp: (a, b) => (a.best_price || 0) - (b.best_price || 0) },
      { key: 'rating', label: 'Highest rated', cmp: (a, b) => (b.rating || 0) - (a.rating || 0) },
    ],
    filters: [
      { key: 'price', type: 'range', label: 'Price', accessor: (o) => num(o.best_price), format: priceFmt },
      { key: 'category', type: 'multi', label: 'Category', accessor: (o) => o.category, optionLabel: pretty },
      { key: 'brand', type: 'multi', label: 'Brand', accessor: (o) => o.brand },
      { key: 'type', type: 'multi', label: 'Product type', accessor: (o) => o.subcategory },
      { key: 'rating', type: 'range', label: 'Rating', accessor: (o) => num(o.rating), format: (v) => `${v}★` },
      { key: 'partner', type: 'multi', label: 'Merchant', accessor: (o) => (o.offers || []).map((x) => x.partner_id), optionLabel: pretty },
      { key: 'instock', type: 'toggle', label: 'In stock', predicate: (o) => (o.offers || []).some((x) => x.availability === 'in_stock') },
    ],
  },
};

function tourDays(o) {
  return o.duration_days || (Array.isArray(o.daily_itinerary) ? o.daily_itinerary.length : 0) || 0;
}
